import {AnswerView} from './answer-view.js';
import { ReviewPanel } from './review-panel.js';
import { MaterialConversation } from './material-conversation.js';
import { CitationPreview } from './citation-preview.js';
import { TracePanel } from './trace-panel.js';
import { pdfOptions } from "./preview-options.js";
import { replayTasks } from "./task-history.js";
import { watchTask } from "./task-stream.js";
import { GraphPanel } from "./graph-panel.js";
import { ProgressPanel, QueryProgressPanel } from "./progress-panel.js";
import { queryProgress, reduceQueryProgress, type QueryProgress } from "./task-progress.js";
import {
  createApp,
  defineComponent,
  h,
  ref,
  shallowRef,
  computed,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { createWebClient } from "@knowyet/sdk/web";
import {
  parseEvidenceResult,
  createEvidenceProgress,
  reduceEvidenceProgress,
  type EvidenceAnswer,
  type EvidenceTrace,
  type EvidenceSource,
  type EvidenceWorkspace,
  type EvidenceWorkspaceDetail,
  type EvidenceOperation,
  type WorkflowInspection,
} from "@knowyet/sdk/evidence";
import type { Identity } from "@knowyet/sdk";
import type { PreviewRequest } from "@knowyet/preview";
import { EvidencePreview } from "@knowyet/ui-vue/evidence";
import { sourcePreview, previewMediaType, RenditionNotice } from './source-preview.js';
import { uploadExtensions } from './source-formats.js';

const web = createWebClient("/api");
// Upload time includes sending the file body; it is independent of query/SSE timeouts.
const uploads = createWebClient("/api", fetch, { connectTimeoutMs: 20 * 60 * 1000 });
const labels: Record<string, string> = {
  source: "材料陈述",
  calculation: "计算结果",
  hypothesis: "条件推断",
  limitation: "证据不足",
  completed: "已完成",
  running: "进行中",
  queued: "排队中",
  cancelled: "已停止",
  failed: "待处理",
  starting: "启动中",
  cancelling: "正在停止",
  reconnecting: "连接中断，正在恢复进度",
};
const stages: Record<string, string> = {
  document: "识别与转换材料",
  directory: "组织材料目录",
  units: "提取信息单元",
  extraction: "抽取材料信息",
  open: "整理材料含义",
  mapping: "映射信息结构",
  schema: "组织结构定义",
  graph: "构建证据图",
  index: "建立检索索引",
  hypothesis: "检索依据与分析",
  inventory: "检查输入材料",
};
const taskErrors: Record<string, string> = {
  human_intervention_required: "材料有待确认事项，请在材料页面处理后继续执行。",
  cancelled: "任务已停止，已保存的步骤可以继续执行。",
  agent_no_candidate:
    "模型结果未通过校验，已保存之前的步骤。可重试当前步骤，或检查材料后重新构建。",
  agent_output_budget: "当前材料的抽取超过模型输出容量，已保存之前的步骤。",
  agent_provider_error: "模型服务调用失败，服务恢复后可继续执行。",
  provider_quota: "模型接口额度已用尽，请补充额度后点击继续执行。已完成的转换和抽取结果已保存。",
  worker_stopped: "处理服务已停止，重启服务后可继续执行。",
  kernel_exit: "处理进程意外退出，可从保存的步骤继续执行。",
  pipeline_version: "保存的任务与当前流程版本不一致，无法直接继续。请保留原项目，将材料上传到新工作区重新构建。",
  workspace_pending: "当前工作区还有未完成的任务，请先继续执行；如果流程版本已更新，请在新工作区重新构建。",
};
function stageName(value: unknown) {
  const s = String(value || "");
  return (
    Object.entries(stages).find(([key]) => s.includes(key))?.[1] || "处理材料"
  );
}
const message = (e: unknown) => {
  const code = e instanceof Error ? e.message : String(e);
  return (
    (
      {
        HTTP_409: "工作区正在使用中，请等待当前任务结束。",
        HTTP_401: "登录已过期，请重新登录。",
        INCOMPLETE_TASK_STREAM: "连接中断，任务可能仍在执行。可重新连接。",
        HTTP_503: "服务暂时不可用，请重试。",
        NETWORK_ERROR: "网络连接中断，自动重连未成功。可点击重新连接，后台任务不会因此停止。",
      } as Record<string, string>
    )[code] || code
  );
};
const roles: Record<string, string> = {
  basis: "依据",
  challenge: "相反材料",
  condition: "条件来源",
  scope: "计算范围",
};
const nodeKinds: Record<string, string> = {
  hypothesis: "条件推断",
  derivation: "推导步骤",
  statement: "回答陈述",
};
function derivation(node: Record<string, unknown>) {
  return h("article", { class: "derivation" }, [
    h(
      "strong",
      node.method === "calculation"
        ? "确定性计算"
        : nodeKinds[String(node.kind)] || String(node.kind),
    ),
    h("p", String(node.text || node.explanation || node.claim || "")),
    ...(Array.isArray(node.conditions)
      ? node.conditions.map((c) =>
          h(
            "p",
            `条件（${c.origin === "assumption" ? "假设" : c.origin === "user" ? "用户给定" : "材料陈述"}）：${c.text}`,
          ),
        )
      : []),
    ...(Array.isArray(node.gaps)
      ? node.gaps.map((g) => h("p", { class: "muted" }, `待补充：${g}`))
      : []),
    node.plan
      ? h("details", [
          h("summary", "计算范围、操作与实际结果"),
          h(
            "pre",
            JSON.stringify({ plan: node.plan, result: node.result }, null, 2),
          ),
        ])
      : null,
  ]);
}
createApp(
  defineComponent({
    setup() {
      const user = ref<Identity | null>(null),
        authMode = ref("local"),
        username = ref(""),
        password = ref("");
      const workspaces = ref<EvidenceWorkspace[]>([]),
        current = ref<EvidenceWorkspaceDetail | null>(null),
        name = ref("");
      const question = ref(""),
        answer = shallowRef<EvidenceAnswer | null>(null),
        trace = shallowRef<EvidenceTrace | null>(null);
      const traceAnswer = shallowRef<EvidenceAnswer | null>(null);
      const focusedReview = ref<{workspace:string;ids:string[]}|null>(null);
      type ChatMessage = { id: string; question: string; answer?: EvidenceAnswer; draft: string[]; status: string };
      const chats = shallowRef<ChatMessage[]>([]);
      const patchChat = (id: string, patch: Partial<ChatMessage>) => {
        const previous = chats.value.find(m => m.id === id);
        const next = { id, question: previous?.question ?? question.value, draft: [], status: 'running', ...previous, ...patch };
        chats.value = previous ? chats.value.map(m => m.id === id ? next : m) : [...chats.value, next];
      };
      const selected = shallowRef<EvidenceSource | null>(null),
        preview = shallowRef<PreviewRequest | null>(null);
      const error = ref(""),
        status = ref(""),
        activeTask = ref(""),
        pending = ref(false),
        tracing = ref(false),
        evidenceReady = ref(false),
        progress = shallowRef(createEvidenceProgress()),
        queryStatus = shallowRef<QueryProgress | null>(null),
        connected = ref(false),
        graphBusy = ref(false),
        tab = ref("materials"),
        inspection = ref<WorkflowInspection | null>(null);
      const buildAvailability=ref<any>(null), newConversation=ref(false);
      const materialBusy=ref(false);
      const inspectionRequests=ref(0);
      let subscription: AbortController | undefined,
        generation = 0;
      const busy = computed(() => !!activeTask.value || materialBusy.value || inspectionRequests.value>0 || pending.value || tracing.value);
      const built = computed(
        () =>
          evidenceReady.value ||
          chats.value.some(chat => !!chat.answer) ||
          !!inspection.value?.published?.["evidence.snapshot"] ||
          (inspection.value?.workflow_states.some(
            (w) => w.workflow === "evidence.build" && w.status === "completed",
          ) ??
            false),
      );
      const action = async (fn: () => Promise<void>) => {
        error.value = "";
        try {
          await fn();
        } catch (e) {
          error.value = message(e);
        }
      };
      async function reload() {
        workspaces.value = await web.evidence.list();
      }
      async function refresh() {
        const id = current.value?.id,
          seq = generation;
        if (id) {
          const value = await web.evidence.get(id);
          if (seq === generation) current.value = value;
        }
      }
      async function watch(taskId: string, running = true, details = false) {
        subscription?.abort();
        const controller = new AbortController();
        subscription = controller;
        const seq = generation;
        connected.value = false;
        if (running) activeTask.value = taskId;
        const watchedTask = current.value?.tasks.find((t) => t.task_id === taskId);
        let isBuild = watchedTask?.operation === "build";
        if (watchedTask?.operation === "analyze") {
          patchChat(taskId, { question: watchedTask.prompt });
          queryStatus.value = queryProgress(watchedTask.created_at);
          answer.value = null;
          if (running) { trace.value = null; preview.value = null; selected.value = null; }
        }
        let receivedBuildProgress = false;
        try {
          for await (const event of watchTask(
            (id, signal) => web.evidence.watch(id, signal, { progressOnly: true, terminalOnly: !running && !details }),
            taskId,
            controller.signal,
          )) {
            if (seq !== generation || controller.signal.aborted) return;
            if (event.type === "status" && event.status === "reconnecting") {
              connected.value = false;
              status.value = labels.reconnecting;
              continue;
            }
            connected.value = true;
            if (event.type === "status")
              status.value = labels[event.status] || event.status;
            if (event.type === "progress") {
              if (event.progress.operation === "hypothesis.analyze" && !queryStatus.value)
                queryStatus.value = queryProgress(watchedTask?.created_at);
              if (queryStatus.value && !isBuild) queryStatus.value = reduceQueryProgress(queryStatus.value, event.progress);
              if (event.progress.operation === "evidence.build") {
                isBuild = true;
                if (!receivedBuildProgress) {
                  progress.value = createEvidenceProgress(current.value?.files.map(f => f.path) ?? []);
                  receivedBuildProgress = true;
                }
              }
              if (isBuild)
                progress.value = reduceEvidenceProgress(
                  progress.value,
                  event.progress,
                );
              if (event.progress.stage === 'answer.draft') {
                const data = event.progress.data as { statements?: {text?: string}[] };
                patchChat(taskId, { draft: (data?.statements ?? []).filter(s => typeof s.text === 'string').map(s => s.text!), status: 'draft' });
              }
              if (event.progress.stage === 'answer.reset') patchChat(taskId, { draft: [], status: 'checking' });
              if (event.progress.type !== "agent.text")
                status.value = isBuild ? "正在处理材料" : "正在检索与分析";
            }
            if (event.type === "completed") {
              status.value = "已完成";
              error.value = "";
              if (event.result.format === "ehf.build-result/1") { isBuild = true; evidenceReady.value = true; buildAvailability.value=(event.result as Record<string,any>).availability??null; }
              if (isBuild)
                progress.value = { ...progress.value, terminal: "completed" };
              if (event.result.format === "ehf.hypothesis-result/1") {
                if (queryStatus.value) queryStatus.value = { ...queryStatus.value, terminal: "completed" };
                if (running) { trace.value = null; preview.value = null; selected.value = null; }
                answer.value = parseEvidenceResult(event.result);
                patchChat(taskId, { answer: answer.value, draft: [], status: "completed" });
              }
            }
            if (event.type === "failed" || event.type === "cancelled") {
              status.value =
                event.type === "cancelled" ? "已停止，可继续执行" : "执行中断";
              error.value = taskErrors[event.code] || event.message;
              if (!isBuild) patchChat(taskId, { draft: [], status: event.type });
              if (isBuild)
                progress.value = { ...progress.value, terminal: event.type };
              else if (queryStatus.value) queryStatus.value = { ...queryStatus.value, terminal: event.type };
            }
          }
        } catch (e) {
          if (!controller.signal.aborted) error.value = message(e);
        } finally {
          if (seq === generation && !controller.signal.aborted) {
            connected.value = false;
            if (running) {
              pending.value = true;
              try {
                activeTask.value = "";
                await refresh();
                if (seq !== generation) return;
                activeTask.value =
                  current.value?.tasks.find((t) =>
                    [
                      "pending",
                      "queued",
                      "starting",
                      "running",
                      "cancelling",
                    ].includes(t.status),
                  )?.task_id ?? "";
                await inspect();
                // A queued material message may start immediately after this task.
                // Follow that task too; otherwise its busy state never clears.
                const nextTask=activeTask.value;
                if(nextTask&&nextTask!==taskId)void watch(nextTask);
              } finally {
                if (seq === generation) pending.value = false;
              }
            }
          }
        }
      }

      async function inspect() {
        if (!current.value || activeTask.value) return;
        const workspace=current.value.id,seq=generation;
        inspectionRequests.value++;
        try {
          const value=await web.evidence.inspect(workspace);
          if(seq===generation)inspection.value=value;
        } catch (e) {
          if (seq===generation&&!error.value) error.value = message(e);
        } finally {
          inspectionRequests.value--;
        }
      }
      async function choose(id: string) {
        generation++;
        const chosen = generation;
        pending.value = true;
        try {
          subscription?.abort();
          error.value = "";
          activeTask.value = "";
          question.value = "";
          evidenceReady.value = false; buildAvailability.value=null; newConversation.value=false;
          chats.value = [];
          queryStatus.value = null;
          answer.value = null;
          trace.value = null;
          preview.value = null;
          selected.value = null;
          progress.value = createEvidenceProgress(
            current.value?.files.map((f) => f.path) ?? [],
          );
          inspection.value = null;
          current.value = await web.evidence.get(id);
          progress.value = createEvidenceProgress(
            current.value.files.map((f) => f.path),
          );
          history.replaceState(
            null,
            "",
            "?" + new URLSearchParams({ workspace: id }),
          );
          const task = current.value.tasks.find((t) =>
            ["pending", "queued", "starting", "running", "cancelling"].includes(
              t.status,
            ),
          );
          const replay = replayTasks(current.value.tasks).filter(t => t.task_id !== task?.task_id);
          for (const t of replay) {
            if (chosen !== generation) return;
            await watch(t.task_id, false);
          }
          if (chosen !== generation) return;
          if (task) void watch(task.task_id);
          else {
            if (!replay.length) status.value = "就绪";
            if (tab.value === 'materials') await inspect();
          }
        } finally {
          if (chosen === generation) pending.value = false;
        }
      }
      async function loadSession() {
        user.value = await web.auth.session();
        if (user.value) {
          await reload();
          const id = new URL(location.href).searchParams.get("workspace");
          if (id && workspaces.value.some((w) => w.id === id)) await choose(id);
        }
      }
      async function login() {
        const response = await fetch("/api/local-login", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            username: username.value,
            password: password.value,
          }),
        });
        password.value = "";
        if (!response.ok) throw Error("登录失败，请检查账号和密码");
        await loadSession();
      }
      async function run(operation: EvidenceOperation) {
        if (!current.value) return;
        pending.value = true;
        try {
          if(operation.operation==='analyze') {
            const parent=newConversation.value?undefined:chats.value.filter(c=>c.answer).at(-1)?.answer?.analysis_ref;
            operation={...operation,...(parent?{parent_ref:parent}:{})}; newConversation.value=false;
          }
          const task = await web.evidence.run(current.value.id, operation);
          if (operation.operation === "analyze") { patchChat(task.task_id, {question:operation.query}); queryStatus.value = queryProgress(); question.value = ""; }
          if (
            operation.operation === "build" ||
            (operation.operation === "resume" &&
              operation.workflow === "evidence.build")
          )
            progress.value = createEvidenceProgress(
              current.value?.files.map((f) => f.path) ?? [],
            );
          error.value = "";
          trace.value = null;
          preview.value = null;
          if (operation.operation === "analyze") answer.value = null;
          void watch(task.task_id);
        } finally {
          pending.value = false;
        }
      }
      async function upload(event: Event) {
        const input = event.target as HTMLInputElement;
        if (!current.value || !input.files) return;
        const unsupported = Array.from(input.files).filter(f => !uploadExtensions.split(',').includes('.'+f.name.split('.').at(-1)?.toLowerCase()));
        if (unsupported.length) { input.value = ''; throw Error('当前页面暂不支持这些文件：'+unsupported.map(f=>f.name).join('、')); }
        pending.value = true;
        try {
          for (const file of Array.from(input.files)) {
            status.value = `上传 ${file.name}`;
            await uploads.evidence.upload(
              current.value.id,
              file.webkitRelativePath || file.name,
              file,
            );
          }
          await refresh();
          status.value = "上传完成，可以开始构建";
        } finally {
          pending.value = false;
          input.value = "";
        }
      }
      let traceGeneration = 0;
      const traceNode = ref('');
      async function showTrace(nodeId: string, result: EvidenceAnswer) {
        if (!current.value) return;
        const generation = ++traceGeneration, workspaceId = current.value.id;
        trace.value = null; selected.value = null; preview.value = null;
        traceNode.value = nodeId;
        tracing.value = true;
        try {
          const loaded = await web.evidence.trace(
            current.value.id,
            result.analysis_ref,
            nodeId, {view:"graph"},
          );
          if (generation !== traceGeneration || current.value?.id !== workspaceId) return;
          trace.value = loaded;
          traceAnswer.value = result;
          preview.value = null;
          selected.value = null;

        } finally {
          tracing.value = false;
        }
      }
      function showSource(source: EvidenceSource) {
        if (!current.value) return;
        selected.value = source;
        const workspaceId = current.value.id;
        // The preview owns its read-only loading state. Global pending causes
        // unrelated panels to refresh while the PDF is opening.
        preview.value = sourcePreview(source, (ref, signal) => web.files.read(
          { kind: "artifact", workspaceId, ref }, { signal },
        ), workspaceId);
      }
      const button = (
        label: string,
        fn: () => Promise<void> | void,
        disabled = false,
        cls = "",
      ) =>
        h(
          "button",
          {
            class: cls,
            disabled,
            onClick: () =>
              void action(async () => {
                await fn();
              }),
          },
          label,
        );
      onMounted(
        () =>
          void action(async () => {
            authMode.value = (
              await (await fetch("/app-config")).json()
            ).authMode;
            await loadSession();
          }),
      );
      onBeforeUnmount(() => {
        subscription?.abort();
      });
      return () =>
        h("div", { class: "shell" }, [
          h("aside", [
            h("div", { class: "brand" }, [
              "知未",
              h("small", "EVIDENCE WORKSPACE"),
            ]),
            h("p", { class: "muted" }, "从材料出发，让每个回答有出处。"),
            user.value
              ? h("div", { class: "workspace-list" }, [
                  h("h3", "我的工作区"),
                  ...workspaces.value.map((w) =>
                    button(
                      w.name,
                      () => choose(w.id),
                      pending.value || graphBusy.value,
                      current.value?.id === w.id
                        ? "workspace selected"
                        : "workspace",
                    ),
                  ),
                  h(
                    "form",
                    {
                      onSubmit: (e: Event) => {
                        e.preventDefault();
                        void action(async () => {
                          const w = await web.evidence.create(name.value);
                          name.value = "";
                          await reload();
                          await choose(w.id);
                        });
                      },
                    },
                    [
                      h("input", {
                        "aria-label": "工作区名称",
                        placeholder: "新工作区名称",
                        value: name.value,
                        onInput: (e: Event) =>
                          (name.value = (e.target as HTMLInputElement).value),
                      }),
                      h(
                        "button",
                        { disabled: !name.value.trim() },
                        "创建工作区",
                      ),
                    ],
                  ),
                ])
              : null,
            user.value
              ? h("footer", [
                  h("span", user.value.displayName),
                  button("退出", async () => {
                    subscription?.abort();
                    generation++;
                    await web.auth.logout();
                    user.value = null;
                    current.value = null;
                    answer.value = null;
                    trace.value = null;
                    preview.value = null;
                    workspaces.value = [];
                  }),
                ])
              : null,
          ]),
          h("main", [
            h("header", [
              h("div", [
                h("span", { class: "eyebrow" }, "材料 · 检索 · 可追溯回答"),
                h("h1", current.value?.name || "证据工作台"),
              ]),
              h(
                "span",
                { class: "badge", role: "status" },
                status.value || "试用版",
              ),
            ]),
            error.value
              ? h("div", { class: "notice", role: "alert" }, [
                  error.value,
                  button("关闭提示", () => {
                    error.value = "";
                  }),
                ])
              : null,
            !user.value
              ? h("section", { class: "card login" }, [
                  h("h2", "登录工作台"),
                  h("p", "上传材料、提出问题，并查看答案的原始依据。"),
                  authMode.value === "sso"
                    ? h(
                        "a",
                        { class: "primary", href: "/api/auth/sso/start" },
                        "使用知未账号登录",
                      )
                    : h(
                        "form",
                        {
                          onSubmit: (e: Event) => {
                            e.preventDefault();
                            void action(login);
                          },
                        },
                        [
                          h("input", {
                            "aria-label": "账号",
                            autocomplete: "username",
                            placeholder: "账号",
                            value: username.value,
                            onInput: (e: Event) =>
                              (username.value = (
                                e.target as HTMLInputElement
                              ).value),
                          }),
                          h("input", {
                            "aria-label": "密码",
                            type: "password",
                            autocomplete: "current-password",
                            placeholder: "密码",
                            value: password.value,
                            onInput: (e: Event) =>
                              (password.value = (
                                e.target as HTMLInputElement
                              ).value),
                          }),
                          h("button", { class: "primary" }, "登录"),
                        ],
                      ),
                ])
              : !current.value
                ? h("section", { class: "card empty" }, [
                    h("h2", "建立你的第一个证据工作区"),
                    h(
                      "p",
                      "在左侧创建工作区，将相关材料放在一起。构建完成后，就可以提问和查看原始依据。",
                    ),
                  ])
                : [
                    h(
                      "nav",
                      { class: "workspace-tabs", "aria-label": "工作区视图" },
                      [
                        ...[
                          ["materials", "材料与进度"],
                          ["graph", "图谱探索"],
                          ["answers", "问答与溯源"],
                        ].map(([key, label]) =>
                          button(
                            label,
                            () => {
                              tab.value = key;
                              if (key === 'materials' && !pending.value && !inspection.value) void inspect();
                            },
                            graphBusy.value,
                            tab.value === key ? "selected" : "",
                          ),
                        ),
                      ],
                    ),
                    tab.value === "graph"
                      ? h(GraphPanel, {
                          key:
                            current.value.id +
                            String(busy.value) +
                            String(built.value),
                          workspace: current.value.id,
                          api: web.evidence,
                          onBusy: (value: boolean) => { graphBusy.value = value; },
                          disabled: busy.value || !built.value,
                          loadBlob: (ref: any, signal: AbortSignal) =>
                            web.files.read(
                              {
                                kind: "artifact",
                                workspaceId: current.value!.id,
                                ref,
                              },
                              { signal },
                            ),
                        })
                      : null,
                    h(
                      "section",
                      {
                        class: "card",
                        style: {
                          display: tab.value === "materials" ? "" : "none",
                        },
                      },
                      [
                        h("div", { class: "section-title" }, [
                          h("h2", "01 / 原始材料"),
                          h('p',{class:'muted'},'支持 PDF、Word、Excel、PPT、OpenDocument、文本、CSV/TSV、图片、音频与视频（含 MP4/MOV/AVI/MKV/WebM）。单文件上限 100 MiB。'),
                          h('details', [h('summary','查看支持格式与视频处理范围'),
                            h('p',{class:'muted'},uploadExtensions.replaceAll(',','、')),
                            h('p',{class:'muted'},'视频自动读取时长，无声视频也可构建。默认每 5 秒识别一个画面，长视频自动调整采样间隔；处理首个视频和音轨，保留时间定位，不等同于逐帧或连续动作分析。')]),
                          h("span", `${current.value.files.length} 个文件`),
                        ]),
                        h("div", { class: "toolbar" }, [
                          h("label", { class: "upload" }, [
                            "添加文件",
                            h("input", {
                              type: "file",
                              accept: uploadExtensions,
                              multiple: true,
                              disabled: busy.value,
                              "aria-label": "添加文件",
                              onChange: (e: Event) =>
                                void action(() => upload(e)),
                            }),
                          ]),
                          h("label", { class: "upload" }, [
                            "添加文件夹",
                            h("input", {
                              type: "file",
                              webkitdirectory: "",
                              multiple: true,
                              disabled: busy.value,
                              "aria-label": "添加文件夹",
                              onChange: (e: Event) =>
                                void action(() => upload(e)),
                            }),
                          ]),
                          button(
                            "开始构建",
                            () => run({ operation: "build" }),
                            busy.value || !current.value.files.length,
                            "primary",
                          ),
                          activeTask.value
                            ? button("停止任务", () =>
                                web.evidence.cancel(activeTask.value),
                              )
                            : null,
                          button(
                            "重新连接",
                            () => choose(current.value!.id),
                            pending.value,
                          ),
                          button('加载历史处理进度', async () => {
                            const seq = generation;
                            pending.value = true;
                            try {
                              for (const task of replayTasks(current.value!.tasks).filter(t => t.operation === 'build' || t.operation === 'resume')) {
                                if (seq !== generation) return;
                                await watch(task.task_id, false, true);
                              }
                            } finally { if (seq === generation) pending.value = false; }
                          }, busy.value),
                        ]),
                        h(
                          "ul",
                          { class: "files" },
                          current.value.files.map((f) =>
                            h("li", [
                              h("span", f.path),
                              h("small", `${Math.ceil(f.bytes / 1024)} KB`),
                            ]),
                          ),
                        ),
                        ...(inspection.value?.workflow_states
                          .filter((w) => w.status !== "completed")
                          .map((w) =>
                            h("div", { class: "notice" }, [
                              h(
                                "span",
                                `${w.last_error?.code === "human_intervention_required" ? "等待材料确认" : stageName(w.stage)+"已中断"}。已保存 ${w.completed.length} 个步骤。`,
                              ),
                              button(
                                "继续执行",
                                () =>
                                  run({
                                    operation: "resume",
                                    workflow: w.workflow as
                                      "evidence.build" | "hypothesis.analyze",
                                    instance: w.instance,
                                  }),
                                busy.value,
                              ),
                            ]),
                          ) || []),
                        buildAvailability.value?.status==='partial'?h('section',{class:'notice','data-testid':'build-issues'},[
                          h('h3','构建已完成，部分材料需要处理'),h('p','可用材料已可提问。可以通过下方对话处理待确认事项。'),
                          ...buildAvailability.value.files.filter((f:any)=>f.status!=='ready').map((f:any)=>h('details',[h('summary',f.file+' · '+(f.status==='blocked'?'文件不可用':'可检索，存在待办')),...f.issues.map((i:any)=>h('p',i.message))]))
                        ]):null,
                        h(MaterialConversation,{workspace:current.value.id,client:web.evidence,busy:busy.value,
                          reviewIds:focusedReview.value?.workspace===current.value.id?focusedReview.value.ids:[],
                          onBusy:(value:boolean)=>{materialBusy.value=value;},onClearFocus:()=>{focusedReview.value=null;},
                          onUpdated:()=>{void refresh().then(()=>inspect());}}),
                        tab.value === 'materials' ? h(ReviewPanel,{workspace:current.value.id,busy:busy.value,
                          refreshKey:JSON.stringify(inspection.value?.workflow_states),onSource:showSource,
                          reviewIds:focusedReview.value?.workspace===current.value.id?focusedReview.value.ids:[],onClearFocus:()=>{focusedReview.value=null;},
                          onDiscuss:(ids:string[])=>{focusedReview.value={workspace:current.value!.id,ids};document.querySelector('.material-conversation')?.scrollIntoView({behavior:'smooth'});},
                          onResolved:()=>void refresh()}) : null,
                        Object.keys(progress.value.steps).length
                          ? h(ProgressPanel, {
                              state: progress.value,
                              connected: connected.value,
                            })
                          : null,
                      ],
                    ),
                    h(
                      "section",
                      {
                        class: "card",
                        style: {
                          display: tab.value === "answers" ? "" : "none",
                        },
                      },
                      [
                        h("h2", "与材料对话"),
                        h('button',{disabled:busy.value,onClick:()=>{newConversation.value=true;}},'开始新对话'),
                        h('p',{class:'muted'},newConversation.value?'下一问将开启新对话，不沿用此前上下文。':'继续提问可沿用上一轮问题的上下文；本轮引用仍需重新核对。'),
                        h('p', {class:'muted'}, '直接提问。点击回答中的引用数字，查看原文依据和溯源关系。'),
                        h('div', {class:'chat-messages','data-testid':'chat-messages','aria-live':'polite'}, chats.value.length ? chats.value.map(chat=>h('article',{class:'chat-turn','data-chat-id':chat.id},[
                          h('div',{class:'chat-user'},chat.question),
                          h('div',{class:'chat-assistant'}, chat.answer ? [
                            h(AnswerView,{answer:chat.answer,loadArtifact:(ref:any,signal:AbortSignal)=>web.files.read({kind:'artifact',workspaceId:current.value!.id,ref},{signal}),onCorrect:()=>{focusedReview.value=null;tab.value='materials';void inspect();},disabled:!!activeTask.value||tracing.value,onTrace:(id:string)=>action(()=>showTrace(id,chat.answer!)),onReview:(ids:string[])=>{focusedReview.value={workspace:current.value!.id,ids:[...new Set(ids)]};tab.value='materials';void inspect();}}),
                          ]:chat.draft.length?[
                            h('div',{'data-testid':'answer-draft'},chat.draft.map(text=>h('p',text))),h('small',{class:'muted'},'正在组织答案，引用尚在核对…'),
                          ]:[h('p',{class:'chat-status'},chat.status==='failed'?'本次回答中断，请查看错误后继续。':chat.status==='cancelled'?'已停止。':chat.status==='checking'?'正在核对并修正引用…':'正在查找相关材料…')]),
                        ])):h('div',{class:'chat-empty'},'例如：债权申报截止日期是什么时候？')),
                        queryStatus.value && activeTask.value ? h(QueryProgressPanel, { state: queryStatus.value, connected: connected.value }) : null,
                        h(
                          "form",
                          {
                            onSubmit: (e: Event) => {
                              e.preventDefault();
                              void action(() =>
                                run({
                                  operation: "analyze",
                                  query: question.value,
                                }),
                              );
                            },
                          },
                          [
                            h("textarea", {
                              "aria-label": "问题",
                              placeholder: built.value
                                ? "例如：法院指定的管理人是谁？请列出原始依据。"
                                : "构建完成后即可提问",
                              value: question.value,
                              disabled: busy.value || !built.value,
                              onInput: (e: Event) =>
                                (question.value = (
                                  e.target as HTMLTextAreaElement
                                ).value),
                            }),
                            h(
                              "button",
                              {
                                class: "primary",
                                disabled:
                                  busy.value ||
                                  !built.value ||
                                  !question.value.trim(),
                              },
                              "提问",
                            ),
                          ],
                        ),

                      ],
                    ),
                    trace.value && tab.value === 'answers' ? h(TracePanel, { trace: trace.value, key: String(trace.value.nodes[0]?.id), onSource: showSource,
                      loadArtifact: (ref:any,signal:AbortSignal)=>web.files.read({kind:'artifact',workspaceId:current.value!.id,ref},{signal}),
                      loadPage: async (offset: number) => web.evidence.trace(current.value!.id, traceAnswer.value!.analysis_ref, traceNode.value, {offset}),
                      loadGraph: async () => web.evidence.trace(current.value!.id, traceAnswer.value!.analysis_ref, traceNode.value, {view:'graph'}),
                      loadCitation: async (id: string, offset = 0) => {
                        if (!current.value || !traceAnswer.value) throw Error('未选择回答');
                        const focused = await web.evidence.trace(current.value.id, traceAnswer.value.analysis_ref, id, {offset});
                        return focused;
                      },
                      onClose: () => { trace.value = null; selected.value = null; preview.value = null; } }) : null,
                    selected.value && preview.value ? h('div', {class:'source-modal',role:'dialog','aria-label':'回答原文依据','data-testid':'answer-preview'},[
                      h('div',{class:'source-modal-header'},[h('h3',selected.value.file),button('关闭原文',()=>{selected.value=null;preview.value=null;})]),
                      h('blockquote',{'data-testid':'selected-quote'},selected.value.text),
                      h('p',{class:'muted'},'仅标注当前引用片段。定位精度取决于原件提供的文字行或识别区域。'),
                      h('p',{class:'muted'},'关闭原文可回到关系图，选择其他引文查看其依据。'),
                      h(RenditionNotice,{source:selected.value,loadOriginal:(signal:AbortSignal)=>web.files.read({kind:'artifact',workspaceId:current.value!.id,ref:selected.value!.original},{signal})}),
                      h(CitationPreview,{source:selected.value,fallback:preview.value,load:(ref:any,signal:AbortSignal)=>web.files.read({kind:'artifact',workspaceId:current.value!.id,ref},{signal})}),
                    ]):null,

                  ],
          ]),
        ]);
    },
  }),
).mount("#app");
